import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '@/i18n/LanguageContext';
import { dataProvider } from '@/lib/dataProvider';
import moduleService from './services/moduleService';
import { Module, ModuleSection, ModuleActivity } from '@/modules/lms/modules/apis/moduleApi';

const ModuleEditPage: React.FC = () => {
  const { moduleId } = useParams<{ moduleId: string }>();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const isEditMode = !!moduleId;
  
  // State for module form
  const [moduleForm, setModuleForm] = useState<any>({
    module_code: '',
    name: '',
    description: '',
    subject: '',
    tags: '',
    status: 'Draft',
  });

  // State for sections
  const [sections, setSections] = useState<ModuleSection[]>([]);

  // State for a new section being added
  const [newSection, setNewSection] = useState<{
    name: string;
    delivery_method: 'e-learning' | 'live-online' | 'offline';
  }>({
    name: '',
    delivery_method: 'e-learning',
  });

  // State for a new activity being added to a section
  const [newActivity, setNewActivity] = useState<{
    sectionIndex: number | null;
    type: 'lesson' | 'exercise' | 'session' | 'quiz' | 'assignment';
    title: string;
  }>({
    sectionIndex: null,
    type: 'lesson',
    title: '',
  });

  // State for loading and errors
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // Load module data if in edit mode
  useEffect(() => {
    if (isEditMode) {
      setIsLoading(true);
      
      const fetchModule = async () => {
        try {
          if (moduleId) {
            const moduleData = await moduleService.getModuleEdit(moduleId);
            
            if (moduleData) {
              setModuleForm({
                module_code: moduleData.module_code,
                name: moduleData.name,
                description: moduleData.description || '',
                subject: moduleData.subject || '',
                tags: moduleData.tags?.join(', ') || '',
                status: moduleData.status,
              });
              setSections(moduleData.sections || []);
              setError(null);
            } else {
              setError('Module không tồn tại');
            }
          } else {
            setError('Module không tồn tại');
          }
        } catch (error) {
          console.error('Error fetching module:', error);
          setError('Có lỗi xảy ra khi tải thông tin module');
        } finally {
          setIsLoading(false);
        }
      };
      
      fetchModule();
    }
  }, [moduleId, isEditMode]);

  // Handle module form changes
  const handleModuleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setModuleForm((prev: any) => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle section form changes
  const handleNewSectionChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setNewSection((prev: any) => ({
      ...prev,
      [name]: value
    }));
  };

  // Add a new section
  const handleAddSection = () => {
    if (newSection.name.trim() === '') {
      return;
    }
    
    const newSectionObj = {
      section_id: `SECTION${Date.now()}`,
      name: newSection.name,
      order: sections.length + 1,
      delivery_method: newSection.delivery_method,
      activities: []
    };
    
    setSections([...sections, newSectionObj]);
    setNewSection({
      name: '',
      delivery_method: 'e-learning',
    });
  };

  // Remove a section
  const handleRemoveSection = (sectionId: string) => {
    const updatedSections = sections.filter(section => section.section_id !== sectionId);
    // Update order numbers
    const reorderedSections = updatedSections.map((section, index) => ({
      ...section,
      order: index + 1
    }));
    setSections(reorderedSections);
  };

  // Move section up or down
  const handleMoveSection = (sectionId: string, direction: 'up' | 'down') => {
    const sectionIndex = sections.findIndex(section => section.section_id === sectionId);
    if (
      (direction === 'up' && sectionIndex === 0) || 
      (direction === 'down' && sectionIndex === sections.length - 1)
    ) {
      return;
    }

    const newSections = [...sections];
    const targetIndex = direction === 'up' ? sectionIndex - 1 : sectionIndex + 1;
    
    // Swap sections
    [newSections[sectionIndex], newSections[targetIndex]] = [newSections[targetIndex], newSections[sectionIndex]];
    
    // Update order numbers
    const reorderedSections = newSections.map((section, index) => ({
      ...section,
      order: index + 1
    }));
    
    setSections(reorderedSections);
  };

  // Handle activity form changes
  const handleNewActivityChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>, sectionIndex: number) => {
    const { name, value } = e.target;
    setNewActivity((prev: any) => ({
      ...prev,
      sectionIndex,
      [name]: value
    }));
  };

  // Add a new activity to a section
  const handleAddActivity = (sectionIndex: number) => {
    if (newActivity.title.trim() === '' || newActivity.sectionIndex !== sectionIndex) {
      return;
    }
    
    const newActivityObj = {
      activity_id: `ACT${Date.now()}`,
      type: newActivity.type,
      title: newActivity.title
    };
    
    const updatedSections = [...sections];
    updatedSections[sectionIndex].activities.push(newActivityObj);
    
    setSections(updatedSections);
    setNewActivity({
      sectionIndex: null,
      type: 'lesson',
      title: ''
    });
  };

  // Remove an activity
  const handleRemoveActivity = (sectionIndex: number, activityId: string) => {
    const updatedSections = [...sections];
    updatedSections[sectionIndex].activities = updatedSections[sectionIndex].activities.filter(
      (activity: any) => activity.activity_id !== activityId
    );
    setSections(updatedSections);
  };

  // Save the module
  const handleSaveModule = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    if (moduleForm.name.trim() === '' || moduleForm.module_code.trim() === '') {
      setError('Vui lòng điền đầy đủ thông tin bắt buộc');
      return;
    }
    
    setIsSaving(true);
    
    // Prepare module data
    const moduleData: Partial<Module> = {
      ...moduleForm,
      tags: moduleForm.tags.split(',').map((tag: string) => tag.trim()).filter((tag: string) => tag !== ''),
      sections,
      delivery_methods: Array.from(new Set(sections.map(section => section.delivery_method)))
    };
    
    try {
      if (isEditMode && moduleId) {
        // Update existing module
        const updatedModule = await moduleService.updateModule(moduleId, moduleData);
        navigate(`/lms/modules/${moduleId}`);
      } else {
        // Create new module
        const newModule = await moduleService.createModule(moduleData);
        navigate(`/lms/modules/${newModule.module_id}`);
      }
    } catch (error) {
      console.error('Error saving module:', error);
      setError('Có lỗi xảy ra khi lưu module. Vui lòng thử lại sau.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">
          {isEditMode ? 'Chỉnh sửa Module' : 'Tạo Module mới'}
        </h1>
        <div className="flex gap-2">
          <Link
            to={isEditMode ? `/lms/modules/${moduleId}` : '/lms/modules'}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            Hủy
          </Link>
          <button
            type="button"
            onClick={handleSaveModule}
            disabled={isSaving}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300"
          >
            {isSaving ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Đang lưu...
              </>
            ) : (
              'Lưu Module'
            )}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSaveModule} className="space-y-8">
        {/* Module Information */}
        <div className="bg-white shadow-sm rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Thông tin Module</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="module_code" className="block text-sm font-medium text-gray-700 mb-1">
                Mã Module <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="module_code"
                id="module_code"
                value={moduleForm.module_code}
                onChange={handleModuleFormChange}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                required
              />
            </div>
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                Trạng thái
              </label>
              <select
                id="status"
                name="status"
                value={moduleForm.status}
                onChange={handleModuleFormChange}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="Draft">Nháp</option>
                <option value="Published">Đã xuất bản</option>
                <option value="Archived">Đã lưu trữ</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Tên Module <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                id="name"
                value={moduleForm.name}
                onChange={handleModuleFormChange}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                required
              />
            </div>
            <div className="md:col-span-2">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                Mô tả
              </label>
              <textarea
                name="description"
                id="description"
                rows={4}
                value={moduleForm.description}
                onChange={handleModuleFormChange}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
            <div className="md:col-span-2">
              <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                Môn học
              </label>
              <input
                type="text"
                name="subject"
                id="subject"
                value={moduleForm.subject}
                onChange={handleModuleFormChange}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
            <div className="md:col-span-2">
              <label htmlFor="tags" className="block text-sm font-medium text-gray-700 mb-1">
                Tags (phân cách bằng dấu phẩy)
              </label>
              <input
                type="text"
                name="tags"
                id="tags"
                value={moduleForm.tags}
                onChange={handleModuleFormChange}
                placeholder="Ví dụ: quản lý dự án, cơ bản, kỹ năng"
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
          </div>
        </div>

        {/* Module Structure */}
        <div className="bg-white shadow-sm rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-medium text-gray-900">Cấu trúc nội dung</h2>
            <span className="text-sm text-gray-500">{sections.length} sections</span>
          </div>

          {/* Sections list */}
          <div className="space-y-4 mb-6">
            {sections.map((section, sectionIndex) => (
              <div key={section.section_id} className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-3 flex justify-between items-center">
                  <div className="flex items-center">
                    <span className="bg-gray-200 text-gray-700 rounded-full h-6 w-6 flex items-center justify-center text-sm font-medium mr-3">
                      {section.order}
                    </span>
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">{section.name}</h4>
                      <p className="text-xs text-gray-500 capitalize">{section.delivery_method}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={() => handleMoveSection(section.section_id, 'up')}
                      disabled={sectionIndex === 0}
                      className="text-gray-400 hover:text-gray-600 disabled:text-gray-300"
                    >
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleMoveSection(section.section_id, 'down')}
                      disabled={sectionIndex === sections.length - 1}
                      className="text-gray-400 hover:text-gray-600 disabled:text-gray-300"
                    >
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRemoveSection(section.section_id)}
                      className="text-red-400 hover:text-red-600"
                    >
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
                
                {/* Activities */}
                <div className="divide-y divide-gray-200">
                  {section.activities.map((activity: any, activityIndex: any) => (
                    <div key={activity.activity_id} className="px-4 py-3 flex justify-between items-center">
                      <div className="flex items-center">
                        <div className="bg-blue-100 text-blue-700 p-1 rounded-md mr-3">
                          {activity.type === 'lesson' && (
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                            </svg>
                          )}
                          {activity.type === 'exercise' && (
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                            </svg>
                          )}
                          {activity.type === 'session' && (
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                          )}
                        </div>
                        <div>
                          <h5 className="text-sm font-medium text-gray-900">{activity.title}</h5>
                          <p className="text-xs text-gray-500 capitalize">{activity.type}</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveActivity(sectionIndex, activity.activity_id)}
                        className="text-red-400 hover:text-red-600"
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
                
                {/* Add activity form */}
                <div className="bg-gray-50 px-4 py-3">
                  <h5 className="text-xs font-medium text-gray-700 mb-2">Thêm hoạt động mới</h5>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <select
                      name="type"
                      value={newActivity.sectionIndex === sectionIndex ? newActivity.type : 'lesson'}
                      onChange={(e) => handleNewActivityChange(e, sectionIndex)}
                      className="block w-full sm:w-1/4 border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
                    >
                      <option value="lesson">Bài học</option>
                      <option value="exercise">Bài tập</option>
                      <option value="session">Buổi học</option>
                      <option value="quiz">Quiz</option>
                      <option value="assignment">Bài tập lớn</option>
                    </select>
                    <input
                      type="text"
                      name="title"
                      placeholder="Tên hoạt động"
                      value={newActivity.sectionIndex === sectionIndex ? newActivity.title : ''}
                      onChange={(e) => handleNewActivityChange(e, sectionIndex)}
                      className="block w-full sm:w-1/2 border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => handleAddActivity(sectionIndex)}
                      className="inline-flex justify-center items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                    >
                      Thêm
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Add section form */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Thêm section mới</h3>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                name="name"
                placeholder="Tên section"
                value={newSection.name}
                onChange={handleNewSectionChange}
                className="block w-full sm:w-1/2 border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
              <select
                name="delivery_method"
                value={newSection.delivery_method}
                onChange={handleNewSectionChange}
                className="block w-full sm:w-1/4 border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
              >
                <option value="e-learning">E-learning</option>
                <option value="live-online">Live-online</option>
                <option value="offline">Offline</option>
              </select>
              <button
                type="button"
                onClick={handleAddSection}
                className="inline-flex justify-center items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                Thêm Section
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ModuleEditPage; 